import Button from "src/components/Button";
import Layout from "src/components/layout/Layout";
import PaymentListener from "src/components/payment/PaymentListener";
import useAuth from "src/hooks/selectors/useAuth";
import usePaymentRedirect from "src/lib/usePaymentRedirect";
import useSubscription from "src/hooks/selectors/useSubscription";
import Link from "src/components/Link";
import CompanyGiftCard from "src/components/cards/CompanyGiftCard";
import Card from "src/components/Card";
import TodoList from "src/components/lists/TodoList";
import ExampleTaskList from "src/components/lists/ExampleTaskList";
import NoTasksCard from "src/components/dashboard/NoTasksCard";
import { formatPrice } from "src/lib/formatters";
import { Trans, useTranslation } from "react-i18next";
import SubscriptionEuroBanner from "src/components/promotion/banners/SubscriptionEuroBanner";
import ChristmasGiftCard from "src/components/cards/ChristmasGiftCard";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import CreateTaskButton from "src/components/forms/TaskForm/elements/CreateTaskButton";
import TasksKanban from "src/components/tasks/kanban/TasksKanban";
import RegistrationList from "src/components/lists/RegistrationList";
import MyInboxList from "src/components/lists/MyInboxList";
import NoTeamsCreatedBanner from "src/components/team/NoTeamsCreatedBanner";
import useTeams from "src/hooks/api/services/users/useTeams";

interface DashboardCompanyViewProps {
	messages?: number;
	loading?: boolean;
}

const DashboardCompanyView = ({ loading }: DashboardCompanyViewProps) => {
	const { t } = useTranslation("dashboard-view", {
		keyPrefix: "company",
	});
	const auth = useAuth();
	const { company } = auth;
	const { statuses } = company;
	const subscription = useSubscription();
	const { tenant } = useTenant();
	const { teams } = useTeams();
	return (
		<Layout
			{...{ loading }}
			title={t("title")}
			actions={
				<CreateTaskButton
					small
					variant={
						tenant.modules.includes("intern-task")
							? "INTERN"
							: "CREATE"
					}
				/>
			}
		>
			<div className="flex flex-col flex-1 gap-8">
				<SubscriptionEuroBanner />
				<PaymentListener />
				{auth.mode === "demo" && (
					<div className="p-4 rounded-md z-20 bg-orange">
						<div className="container">
							<Link
								to="/subscription"
								className="text-white mx-auto block text-center"
							>
								<Trans {...{ t }} i18nKey="demo-banner">
									{tenant.name} maakt gebruik van een
									abonnement,
									<span className="bg-dark px-3 underline py-2 mx-2 rounded-md">
										Klik hier
									</span>
									om alle abonnementen te bekijken
								</Trans>
							</Link>
						</div>
					</div>
				)}
				{(auth?.company.statuses.has_gift ||
					subscription?.plan?.slug === "free") && (
					<div className="flex flex-col md:flex-row gap-8">
						{auth?.company.statuses.has_gift && (
							<div className="flex-1">
								<CompanyGiftCard />
							</div>
						)}
						{subscription?.plan?.slug === "free" && <WalletBlock />}
					</div>
				)}
				{/* {!has_subscription && <DashboardCompanyWelcome />} */}
				{/* {has_subscription && ( */}
				<div className="xl:grid flex flex-col flex-1 gap-8 grid-cols-1 xl:grid-cols-3">
					<div className="flex xl:col-span-2 flex-col gap-8">
						{/* <DashboardCompanyHead /> */}
						<>
							{tenant.modules.includes("intern-task") ? (
								<div className="overflow-auto hide-scrollbar flex-1 flex flex-col">
									<TasksKanban />
								</div>
							) : (
								<>
									{statuses.has_tasks ? (
										<div className="grid gap-8 flex-1 grid-cols-1 xl:grid-cols-2">
											<Card
												title={t("cards.registrations")}
											>
												<RegistrationList
													query={`state=available`}
													limit={6}
												/>
											</Card>
											<Card title={t("cards.inbox")}>
												<MyInboxList limit={6} />
											</Card>
										</div>
									) : (
										<div className="flex flex-col flex-1">
											<NoTasksCard />
										</div>
									)}
								</>
							)}
						</>
					</div>
					<div className="flex flex-col md:flex-row xl:flex-col gap-8">
						{auth?.company?.promos?.includes("christmas_gift") && (
							<ChristmasGiftCard />
						)}

						{tenant.modules.includes("intern-task") &&
							teams.length === 0 && <NoTeamsCreatedBanner />}

						<Card
							className="flex-1 xl:flex-none shadow-none"
							title="Taken"
						>
							<TodoList />
						</Card>
						<Card
							className="flex-1 xl:flex-none shadow-none"
							title={t("cards.example-tasks")}
						>
							<ExampleTaskList />
						</Card>
					</div>
				</div>
				{/* )} */}
			</div>
		</Layout>
	);
};

const WalletBlock = () => {
	const { t } = useTranslation("dashboard-view", {
		keyPrefix: "company",
	});
	const [loading] = usePaymentRedirect({
		fetchProfile: true,
	});
	const auth = useAuth();
	const { company } = auth;
	const credit_fee = parseFloat(auth.service_rate);

	return (
		<Card className="flex-1 flex flex-col">
			<div className="flex items-center gap-4">
				<div className="flex flex-col justify-center items-center gap-2">
					<h2>
						<i className="fas fa-wallet"></i>
					</h2>
					<h2>
						{loading ? (
							<span className="base-loader"></span>
						) : (
							formatPrice(company.credit)
						)}
					</h2>
				</div>
				<div className="flex-1 flex flex-col gap-2">
					<p className="max-w-md">
						{t("wallet-block.description", {
							fee: credit_fee * 100,
						})}
					</p>
					<div className="flex">
						<Button
							className="gtm-werkgever-dashboard--cta-tegoed-opwaarderen"
							to="/credits/upgrade"
						>
							{t("wallet-block.button")}
						</Button>
					</div>
				</div>
			</div>
		</Card>
	);
};

export default DashboardCompanyView;
